import { useTranslation } from "react-i18next";
import { Button, Flex, LoadingOverlay, Paper, Table, Text, Title, useMantineColorScheme } from "@mantine/core";
import { openConfirmModal } from "@mantine/modals";
import { Client, ClientUser, ClientUserRole, ClientUserStatus } from "src/graphql/generated/graphql";
import { checkClientLocale } from "src/utils/helpers";

import { useAuthContext } from "../../../../hooks/auth";
import { useClientUsers, useDeactivateClientUser } from "../../../../hooks/clients";
import { InviteClientUserModal } from "../../../../modals/InviteClientUser";

export interface ClientUsersProps {
    client: Client;
}

export const ClientUsers = ({ client }: ClientUsersProps) => {
    const { t } = useTranslation(["common"]);

    const { colorScheme } = useMantineColorScheme();

    const { user: currentUser, client: currentClient } = useAuthContext();

    const { clientUsers, isLoadingClientUsers, reloadClientUsers } = useClientUsers(client?.id);
    const { isDeactivatingClientUser, deactivateClientUser } = useDeactivateClientUser(client?.id);

    // Deactivated users are shown to testuj.to staff only, in the admin, which alone can activate them again.
    // client-api lists active users only; the filter here also covers a lane whose client-api still lists
    // inactive ones on the last page (an API deployed behind this bundle).
    const activeUsers = (clientUsers || []).filter((clientUser) => clientUser?.status === ClientUserStatus.Active);

    // Owners and admins deactivate users; never on their own row, and an admin never on an owner (client-api
    // refuses both, the buttons just do not offer what would fail).
    const callerRole = currentClient?.role;
    const canManage = callerRole === ClientUserRole.Owner || callerRole === ClientUserRole.Admin;
    const canManageUser = (clientUser: ClientUser) =>
        canManage &&
        clientUser.id !== currentUser?.id &&
        !(callerRole === ClientUserRole.Admin && clientUser.role === ClientUserRole.Owner);

    // The mutation answers with the user's id, role and status only, so the list is reloaded.
    const handleDeactivate = (clientUser: ClientUser) => {
        openConfirmModal({
            title: t("clientuser_deactivate_confirm", {
                name: `${clientUser.firstName ?? ""} ${clientUser.lastName ?? ""}`.trim() || clientUser.email,
            }),
            children: <Text>{t("clientuser_deactivate_confirm_description")}</Text>,
            labels: {
                cancel: t("cancel"),
                confirm: t("clientuser_deactivate"),
            },
            confirmProps: { color: "red" },
            onCancel: () => {},
            onConfirm: async () => {
                if (await deactivateClientUser(clientUser.id)) {
                    reloadClientUsers();
                }
            },
        });
    };

    return (
        <Paper pos="relative" p={36} radius={12}>
            <LoadingOverlay visible={isLoadingClientUsers || isDeactivatingClientUser} />
            <Flex justify="space-between" mb={28}>
                <Title order={3} size={16} weight={600}>
                    {t("client_users")}
                </Title>
                <Button variant="subtle" onClick={() => InviteClientUserModal.display({ onSave: reloadClientUsers })}>
                    {t("client_users_invite")}
                </Button>
            </Flex>
            <Table verticalSpacing={20} horizontalSpacing={0}>
                <thead>
                    <tr>
                        <th>
                            <Text size={14} weight={600} color={colorScheme === "dark" ? "gray.3" : "dark"}>
                                {t("fullname")}
                            </Text>
                        </th>
                        <th>
                            <Text size={14} weight={600} color={colorScheme === "dark" ? "gray.3" : "dark"}>
                                {t("clientuser_role")}
                            </Text>
                        </th>
                        <th>
                            <Text size={14} weight={600} color={colorScheme === "dark" ? "gray.3" : "dark"}>
                                {t("email")}
                            </Text>
                        </th>
                        <th>
                            <Text size={14} weight={600} color={colorScheme === "dark" ? "gray.3" : "dark"}>
                                {t("lang")}
                            </Text>
                        </th>
                        {canManage && <th />}
                    </tr>
                </thead>
                <tbody>
                    {activeUsers.map((clientUser, index) => (
                        <tr key={`${index}${clientUser?.id}`}>
                            <td>
                                {clientUser?.firstName} {clientUser?.lastName}
                            </td>
                            <td>{clientUser?.role ? t(`clientuser_role_${clientUser.role}`) : "-"}</td>
                            <td>{clientUser?.email}</td>
                            <td>
                                {clientUser?.locale && checkClientLocale(clientUser?.locale)
                                    ? t(`clientuser_lang_${clientUser.locale}`)
                                    : "-"}
                            </td>
                            {canManage && (
                                <td>
                                    {canManageUser(clientUser) && (
                                        <Button
                                            variant="subtle"
                                            color="red"
                                            compact
                                            onClick={() => handleDeactivate(clientUser)}
                                        >
                                            {t("clientuser_deactivate")}
                                        </Button>
                                    )}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Paper>
    );
};
